import React, { FC, useMemo, useState } from 'react';
import {
  Box,
  createStyles,
  List,
  ListSubheader,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { useApolloClient, useMutation, useQuery } from '@apollo/react-hooks';
import { GET_USER_POSSIBLE_ENTITIES } from '../../../graphql/user/query';
import {
  GetUserPossibleEntities,
  GetUserPossibleEntitiesVariables,
} from '../../../graphql/user/types/GetUserPossibleEntities';
import { capitalizeFirstLetter } from '../../../utils/common';
import { groupByEchelon } from '../../../utils/entity';
import CheckBoxListItem from './List/CheckBoxListItem/CheckBoxListItem';
import {
  AdminEditUser,
  AdminEditUserVariables,
} from '../../../graphql/user/types/AdminEditUser';
import { ADMIN_EDIT_USER } from '../../../graphql/user/mutation';
import { displaySnackBar } from '../../../common/displaySnackbar';
import ConfirmationButtons from '../../ConfirmationButtons/ConfirmationButtons';
import CustomDialog from '../../../common/components/CustomDialog';
import { IEntity } from '../AdditionalInfos/utils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      outline: 'none',
    },
    paper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      backgroundColor: theme.palette.background.paper,
      outline: 'none',
      paddingLeft: 20,
      paddingRight: 20,
      width: 'auto',
    },
    closeContainer: {
      display: 'flex',
      width: '100%',
      justifyContent: 'flex-end',
      marginTop: 10,
    },
    subtitle: {
      textAlign: 'center',
      color: '#9EA3B0',
      fontSize: 16,
      fontStyle: 'italic',
      margin: 20,
    },
    list: {
      width: '100%',
    },
    listSubHeader: {
      height: 15,
      color: 'black',
      paddingLeft: 0,
    },
  }),
);

interface Params {
  userId: number;
  setUserId(id: number | null): void;
}

const ManageEntitiesModal: FC<Params> = (props) => {
  const { userId, setUserId } = props;
  const classes = useStyles();

  const client = useApolloClient();

  const [managedEntityIds, setManagedEntityIds] = useState<number[]>([]);

  const { data, loading } = useQuery<
    GetUserPossibleEntities,
    GetUserPossibleEntitiesVariables
  >(GET_USER_POSSIBLE_ENTITIES, {
    variables: { id: userId },
    onCompleted: (data) => {
      if (
        data &&
        data.getUser &&
        data.getUser.enseigne &&
        data.getUser.enseigne.entities
      ) {
        const managedEntityIds = data.getUser.enseigne.entities
          .filter((entity) =>
            entity.managers?.some((manager) => manager.id === userId),
          )
          .map((entity) => entity.id);
        setManagedEntityIds(managedEntityIds);
      }
    },
  });

  const [editUser, { loading: loadingEditUser }] = useMutation<
    AdminEditUser,
    AdminEditUserVariables
  >(ADMIN_EDIT_USER, {
    onCompleted: () => {
      displaySnackBar(client, {
        type: 'SUCCESS',
        message: 'Utilisateur modifié avec succès',
        isOpen: true,
      });
    },
  });

  const handleClose = () => {
    setUserId(null);
  };

  const handleManageEntity = (
    event: React.ChangeEvent<HTMLInputElement>,
    entityId: number,
  ) => {
    setManagedEntityIds((prev) => {
      const concated = prev ? [...prev, entityId] : [entityId];
      const filtred = prev ? prev.filter((id) => id !== entityId) : [];

      return event.target.checked ? concated : filtred;
    });
  };

  const handleSave = () => {
    editUser({
      variables: {
        input: {
          userId,
          managedEntityIds,
        },
      },
    }).finally(() => handleClose());
  };

  const entityListByEchelon = useMemo(
    () =>
      groupByEchelon(data?.getUser?.enseigne?.entities as Partial<IEntity>[]),
    [data],
  );

  const firstName =
    data && data.getUser && data.getUser.firstName
      ? data.getUser.firstName
      : '';
  const lastName =
    data && data.getUser && data.getUser.lastName ? data.getUser.lastName : '';
  const userName = `${firstName || ''} ${lastName || ''}`;

  return (
    <CustomDialog
      open
      title="Entités supervisées ou monitorées"
      loading={loading}
      onClose={handleClose}
      actions={
        <ConfirmationButtons
          handleCancel={handleClose}
          handleConfirm={handleSave}
          disableConfirm={loadingEditUser || loading}
        />
      }>
      <Box className={classes.paper}>
        {!loading && !entityListByEchelon?.length ? (
          <Typography className={classes.subtitle}>
            Aucune entité trouvée dans l'enseigne de {userName}.
          </Typography>
        ) : (
          <>
            <Box className={classes.subtitle}>
              Choisissez les entités sur lesquelles l'utilisateur {userName}{' '}
              aura une vue statistique.
            </Box>
            {entityListByEchelon?.map((_echelon: any, index: any) => (
              <List
                key={`echelon-list-${index}`}
                subheader={
                  <ListSubheader
                    component="div"
                    className={classes.listSubHeader}>
                    {`${capitalizeFirstLetter(_echelon?.name)} :`}
                  </ListSubheader>
                }
                className={classes.list}>
                {_echelon?.entities?.map((entity: any) => (
                  <CheckBoxListItem
                    key={`entity-list-${entity.id}`}
                    id={entity.id}
                    checked={managedEntityIds.includes(entity.id)}
                    text={entity.name || ''}
                    handleChange={handleManageEntity}
                    disabled={loadingEditUser || loading}
                  />
                ))}
              </List>
            ))}
          </>
        )}
      </Box>
    </CustomDialog>
  );
};

export default ManageEntitiesModal;
